import { useParams, Link, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { getTopROITokens, sectorInfo } from "@/data/mockTokenData";
import { useAffiliateTracking } from "@/hooks/useAffiliateTracking";
import { ArrowLeft, TrendingUp, DollarSign, Target, ChevronLeft, ChevronRight } from "lucide-react";

const allSectors = Object.keys(sectorInfo).filter(s => s !== 'all');

export default function SectorPage() {
  const { sectorId } = useParams<{ sectorId: string }>();
  const navigate = useNavigate();
  const sector = sectorId || "all";
  const { trackAffiliateClick } = useAffiliateTracking();
  
  const sectorData = sectorInfo[sector as keyof typeof sectorInfo] || sectorInfo.all;
  const topTokens = getTopROITokens(sector, 5);

  // Get current sector index for navigation
  const currentIndex = allSectors.indexOf(sector);
  const prevSector = currentIndex > 0 ? allSectors[currentIndex - 1] : allSectors[allSectors.length - 1];
  const nextSector = currentIndex < allSectors.length - 1 ? allSectors[currentIndex + 1] : allSectors[0];

  // Scroll to top when sector changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [sector]);

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <header className="border-b border-border sticky top-0 bg-background/95 backdrop-blur-sm z-50">
        <div className="container mx-auto px-4 py-4">
          <Link to="/">
            <Button variant="ghost" size="sm" className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to All Picks
            </Button>
          </Link>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Sector Hero */}
        <div className="mb-12 text-center">
          <div className="text-6xl mb-4">{sectorData.emoji}</div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
            {sectorData.name}
          </h1>
          <p className="text-xl text-muted-foreground mb-6 max-w-2xl mx-auto">
            {sectorData.description}
          </p>
          <Badge variant="secondary" className="text-lg px-6 py-2">
            Top 5 ROI Gems • Updated {new Date().toLocaleDateString('en-AU')}
          </Badge>
        </div>

        {/* Token Cards */}
        <div className="space-y-6">
          {topTokens.map((token, index) => (
            <div
              key={token.symbol}
              className="bg-card border border-border rounded-2xl p-6 hover:shadow-xl transition-all duration-300 hover:border-primary/50"
            >
              {/* Rank Badge */}
              <div className="flex items-start justify-between mb-4">
                <Badge 
                  variant={index === 0 ? "default" : "secondary"}
                  className="text-lg px-4 py-2"
                >
                  {index === 0 ? "⭐ #1 BEST BUY" : `#${index + 1} Top Pick`}
                </Badge>
                <Badge variant="outline" className="text-sm">
                  {token.sector.toUpperCase()}
                </Badge>
              </div>

              {/* Token Header */}
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center text-2xl font-bold text-primary-foreground">
                  {token.symbol.slice(0, 2)}
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-bold mb-1">{token.name}</h2>
                  <div className="flex items-center gap-3 text-muted-foreground">
                    <span className="font-mono font-semibold">{token.symbol}</span>
                    <span>•</span>
                    <span className={token.change24h > 0 ? "text-success" : "text-destructive"}>
                      {token.change24h > 0 ? "↗" : "↘"} {Math.abs(token.change24h).toFixed(1)}% (24h)
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold">${token.price < 1 ? token.price.toFixed(4) : token.price.toFixed(2)}</div>
                  <div className="text-sm text-muted-foreground">AUD</div>
                </div>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 p-4 bg-muted/30 rounded-xl">
                <div>
                  <div className="text-sm text-muted-foreground mb-1">Market Cap</div>
                  <div className="font-semibold">${(token.marketCap / 1000000).toFixed(0)}M</div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground mb-1">24h Volume</div>
                  <div className="font-semibold">${(token.volume24h / 1000000).toFixed(0)}M</div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground mb-1">7d Change</div>
                  <div className={`font-semibold ${token.change7d > 0 ? "text-success" : "text-destructive"}`}>
                    {token.change7d > 0 ? "+" : ""}{token.change7d.toFixed(1)}%
                  </div>
                </div>
                <div className="md:col-span-1">
                  <div className="text-sm text-muted-foreground mb-1">ROI Potential</div>
                  <div className="text-2xl font-bold text-success">+{token.roiPotential}%</div>
                </div>
              </div>

              {/* Why Buy */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-primary" />
                  Why Buy Now?
                </h3>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  {token.whyBuy}
                </p>
                <div className="space-y-2">
                  {token.catalysts.map((catalyst, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm">
                      <span className="text-success mt-0.5">✅</span>
                      <span>{catalyst}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Price Targets */}
              <div className="mb-6 p-4 bg-success/5 border border-success/20 rounded-xl">
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <Target className="h-5 w-5 text-success" />
                  Price Targets
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">Conservative (3m)</div>
                    <div className="font-bold text-success">${token.targetA < 1 ? token.targetA.toFixed(4) : token.targetA.toFixed(2)}</div>
                    <div className="text-xs text-success">+{(((token.targetA - token.price) / token.price) * 100).toFixed(0)}%</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">Moderate (6m)</div>
                    <div className="font-bold text-success">${token.targetB < 1 ? token.targetB.toFixed(4) : token.targetB.toFixed(2)}</div>
                    <div className="text-xs text-success">+{(((token.targetB - token.price) / token.price) * 100).toFixed(0)}%</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">Bullish (12m)</div>
                    <div className="font-bold text-success">${token.targetC < 1 ? token.targetC.toFixed(4) : token.targetC.toFixed(2)}</div>
                    <div className="text-xs text-success">+{(((token.targetC - token.price) / token.price) * 100).toFixed(0)}%</div>
                  </div>
                </div>
              </div>

              {/* Exchange Availability & Buy Buttons */}
              <div className="space-y-3">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-primary" />
                  Buy {token.symbol} Now
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {token.listedOnCoinspot && (
                    <Button
                      size="lg"
                      onClick={() => {
                        trackAffiliateClick('coinspot');
                        window.open(`https://www.coinspot.com.au?affiliate=72JXN7`, '_blank');
                      }}
                      className="w-full gap-2"
                    >
                      Buy on CoinSpot
                      <span className="text-xs opacity-80">(Get $10 Bonus)</span>
                    </Button>
                  )}
                  {token.listedOnSwyftx && (
                    <Button
                      size="lg"
                      variant="secondary"
                      onClick={() => {
                        trackAffiliateClick('swyftx');
                        window.open(`https://trade.swyftx.com/register/?ref=davidrealestate`, '_blank');
                      }}
                      className="w-full gap-2"
                    >
                      Buy on Swyftx
                      <span className="text-xs opacity-80">(Get $20 Bonus)</span>
                    </Button>
                  )}
                  {!token.listedOnCoinspot && !token.listedOnSwyftx && (
                    <Button
                      size="lg"
                      variant="outline"
                      onClick={() => {
                        trackAffiliateClick('independent_reserve');
                        window.open(`https://www.independentreserve.com`, '_blank');
                      }}
                      className="w-full"
                    >
                      Buy on Other Exchanges
                    </Button>
                  )}
                </div>
                {(!token.listedOnCoinspot || !token.listedOnSwyftx) && (
                  <p className="text-xs text-muted-foreground">
                    {!token.listedOnCoinspot && !token.listedOnSwyftx 
                      ? `${token.symbol} is not yet available on major Australian exchanges. Use global exchanges like Binance or KuCoin.`
                      : `Also available on other exchanges`
                    }
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center p-8 bg-gradient-to-r from-primary/10 via-accent/10 to-primary/10 rounded-2xl border border-primary/20">
          <h2 className="text-2xl font-bold mb-4">Get Tomorrow's Picks at 6 AM AEST</h2>
          <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
            Join 2,500+ Aussies getting daily crypto alerts before the market moves
          </p>
          <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input
              type="email"
              placeholder="Your email"
              className="flex-1 px-4 py-3 rounded-lg border border-border bg-background"
            />
            <Button size="lg" className="whitespace-nowrap">
              Subscribe Free →
            </Button>
          </div>
        </div>
      </main>

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-background/95 backdrop-blur-sm border-t border-border z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between max-w-4xl mx-auto">
            <Button
              variant="outline"
              onClick={() => navigate(`/sector/${prevSector}`)}
              className="flex items-center gap-2"
            >
              <ChevronLeft className="w-4 h-4" />
              <span className="hidden sm:inline">{sectorInfo[prevSector as keyof typeof sectorInfo]?.name || 'Previous'}</span>
              <span className="sm:hidden">Previous</span>
            </Button>

            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="text-sm"
            >
              All Sectors
            </Button>

            <Button
              variant="outline"
              onClick={() => navigate(`/sector/${nextSector}`)}
              className="flex items-center gap-2"
            >
              <span className="hidden sm:inline">{sectorInfo[nextSector as keyof typeof sectorInfo]?.name || 'Next'}</span>
              <span className="sm:hidden">Next</span>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
